import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, AlertTriangle, Check, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { CONSENT_LANGS } from "../lib/helpers";
import { PageHeader, Empty, Field, NativeSelect } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ProfileFields, PROFILE_BLANK } from "../components/ProfileFields";

const BLANK = { first_name: "", last_name: "", email: "", phone: "", ...PROFILE_BLANK, preferred_language: "en",
  source: "", allergies: "", medications: "", conditions: "", attention_notes: "", marketing_consent: false, whatsapp_opt_in: false, notes: "" };
const SOURCES = ["", "word_of_mouth", "google", "instagram", "facebook", "campaign", "online", "other"];

export function ClientEditor({ open, onOpenChange, client, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState(BLANK);
  useEffect(() => { if (open) setF({ ...BLANK, ...(client || {}) }); }, [open, client]);
  const inp = (k, type = "text") => <Input data-testid={`client-${k}-input`} type={type} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />;
  const area = (k) => <Textarea data-testid={`client-${k}-input`} rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />;

  const save = async () => {
    try {
      const body = Object.fromEntries(Object.keys(BLANK).map((k) => [k, f[k]]));
      const { data } = client?.id ? await api.put(`/clients/${client.id}`, body) : await api.post("/clients", body);
      toast.success("Client saved");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="client-editor">
        <DialogHeader><DialogTitle>{client?.id ? t("edit") : t("new_client")}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("first_name")}>{inp("first_name")}</Field>
          <Field label={t("last_name")}>{inp("last_name")}</Field>
          <Field label={t("email")}>{inp("email", "email")}</Field>
          <Field label={t("phone")}>{inp("phone", "tel")}</Field>
          <ProfileFields value={f} onChange={setF} t={t} />
          <Field label={t("language")}>
            <NativeSelect testId="client-language-select" value={f.preferred_language} onChange={(v) => setF({ ...f, preferred_language: v })}
              options={Object.entries(CONSENT_LANGS).map(([v, label]) => ({ value: v, label }))} />
          </Field>
          <Field label="Tax code (for invoices)">{inp("tax_code")}</Field>
          <Field label={t("source")}>
            <NativeSelect testId="client-source-select" value={f.source} onChange={(v) => setF({ ...f, source: v })}
              options={SOURCES.map((s) => ({ value: s, label: s ? s.replace(/_/g, " ") : "—" }))} />
          </Field>
          <label className="flex items-center gap-3 text-sm pt-6">
            <Switch data-testid="client-marketing-switch" checked={f.marketing_consent} onCheckedChange={(v) => setF({ ...f, marketing_consent: v })} />{t("marketing")}
          </label>
          <label className="flex items-center gap-3 text-sm sm:col-start-2">
            <Switch data-testid="client-whatsapp-optin-switch" checked={f.whatsapp_opt_in} onCheckedChange={(v) => setF({ ...f, whatsapp_opt_in: v })} />{t("whatsapp_opt_in")}
          </label>
          <div className="sm:col-span-2 rounded-[var(--radius)] border border-amber-500/40 bg-amber-500/5 p-4 grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" />{t("needs_to_know")}</div>
            <Field label={t("allergies")}>{area("allergies")}</Field>
            <Field label={t("medications")}>{area("medications")}</Field>
            <Field label={t("conditions")}>{area("conditions")}</Field>
            <Field label={t("attention")}>{area("attention_notes")}</Field>
          </div>
          <Field label={t("notes")} className="sm:col-span-2">{area("notes")}</Field>
        </div>
        <Button data-testid="client-save-button" className="rounded-full mt-2" disabled={!f.first_name} onClick={save}>{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function PendingPatients({ onChanged }) {
  const { can } = useApp();
  const [list, setList] = useState([]);
  const load = () => api.get("/clients", { params: { status: "pending" } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const decide = (c, approve) => api.post(`/clients/${c.id}/portal-approval`, { approve })
    .then(() => { toast.success(approve ? "Approved – the patient has been emailed" : "Rejected"); load(); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  if (!list.length) return null;
  return (
    <div className="rounded-[var(--radius)] border border-primary/30 bg-primary/5 p-4 mb-6 space-y-2" data-testid="pending-patients">
      <p className="font-semibold text-sm">Pending approval ({list.length})</p>
      {list.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3 text-sm" data-testid={`pending-${c.id}`}>
          <span className="flex-1 min-w-[180px]"><b>{c.first_name} {c.last_name}</b> <span className="text-muted-foreground">· {c.email}{c.phone && ` · ${c.phone}`} · {c.created_at?.slice(0, 10)}</span></span>
          {can("clients.write") && <>
            <Button size="sm" className="rounded-full" data-testid={`pending-approve-${c.id}`} onClick={() => decide(c, true)}><Check className="h-4 w-4 mr-1" />Approve</Button>
            <Button size="sm" variant="ghost" data-testid={`pending-reject-${c.id}`} onClick={() => decide(c, false)}><X className="h-4 w-4 mr-1" />Reject</Button></>}
        </div>))}
    </div>
  );
}

export default function Clients() {
  const { can } = useApp();
  const { t } = useT();
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/clients", { params: { q } }).then((r) => setList(r.data));
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <PageHeader eyebrow={`${list?.length ?? 0}`} title={t("clients")}>
        {can("clients.write") && <Button className="rounded-full" data-testid="new-client-btn" onClick={() => setAdding(true)}>{t("new_client")}</Button>}
      </PageHeader>
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input data-testid="clients-search-input" className="pl-9" placeholder={`${t("search")}: ${t("name")}, ${t("phone")}, ${t("email")}, code`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <PendingPatients onChanged={load} />
      {list && !list.length && <Empty text={t("no_data")} testId="clients-empty" />}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 stagger" data-testid="clients-list">
        {list?.map((c) => (
          <Link key={c.id} to={`/app/clients/${c.id}`} data-testid={`client-card-${c.id}`} className="lift rounded-[var(--radius)] border bg-card p-5 flex items-start gap-4">
            <div className="h-11 w-11 rounded-full bg-primary/10 text-primary grid place-items-center font-display font-bold">
              {(c.first_name[0] || "") + (c.last_name?.[0] || "")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{c.first_name} {c.last_name}{c.portal_status === "pending" && <span className="ml-2 text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">Pending</span>}{c.portal_status === "rejected" && <span className="ml-2 text-xs rounded-full bg-muted px-2 py-0.5">Rejected</span>}</div>
              <div className="text-xs text-muted-foreground truncate">{c.client_code} · {c.phone || c.email}</div>
            </div>
            {(c.allergies || c.medications || c.attention_notes) && <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />}
          </Link>
        ))}
      </div>
      <ClientEditor open={adding} onOpenChange={setAdding} onSaved={load} />
    </div>
  );
}
